import { useEffect, useMemo, useState } from 'react';
import {
  AutomationType,
  AutomationActionType,
  CampaignStatus
} from 'generated-graphql/types';
import {
  useGetAutomationTasksByAutomationTypeQuery,
  useGetCampaignByIdLazyQuery
} from 'generated-graphql/hooks/apollo-hooks';
import { extractListItems } from 'core/utils/apollo/extractListItems';

export const useArchivedActionCampaign = (candidateBatchId?: string) => {
  const [campaigns, setCampaigns] = useState<
    { title?: string | null; status: string }[]
  >([]);

  const { data: automationData } = useGetAutomationTasksByAutomationTypeQuery({
    variables: {
      candidateBatchId: candidateBatchId || '',
      automationType: AutomationType.Action
    },
    skip: !candidateBatchId
  });

  const campaignIds = useMemo(() => {
    const tasks = extractListItems(
      automationData?.getAutomationTasksByAutomationType?.automationTasks ?? []
    );

    return [
      ...new Set(
        extractListItems(
          tasks
            .filter(
              (task) =>
                task.action?.type === AutomationActionType.SendCampaign &&
                task.action?.campaignId
            )
            .map((task) => task.action?.campaignId)
        )
      )
    ];
  }, [automationData]);

  const [getCampaign] = useGetCampaignByIdLazyQuery();

  useEffect(() => {
    if (campaignIds.length === 0) {
      setCampaigns([]);
      return;
    }

    const fetchCampaigns = async () => {
      const results = await Promise.all(
        campaignIds.map((campaignId) =>
          getCampaign({ variables: { campaignId } })
        )
      );

      setCampaigns(
        extractListItems(
          results.map((result) => result.data?.getCampaign ?? null)
        )
      );
    };

    fetchCampaigns();
  }, [campaignIds, getCampaign]);

  const archivedCampaignNames = useMemo(() => {
    return extractListItems(
      campaigns
        .filter((campaign) => campaign.status === CampaignStatus.Archived)
        .map((campaign) => campaign.title ?? null)
    );
  }, [campaigns]);

  return { archivedCampaignNames };
};
